/**
 * useActionReadiness — czy dla danej firmy + klasy dokumentu spełnione są warunki
 * konfiguracji akcji zbramkowanych (PHASE 25 Faza 2):
 *  - `ksefConfigured` — istnieje połączenie KSeF (moduł `ksef_integration`; brak modułu /
 *    uprawnienia traktujemy jak „nieskonfigurowane", nie jak błąd),
 *  - `fkConfigured` — istnieje aktywna integracja ERP/Symfonia (moduł `symfonia_integration`),
 *  - `hasChart` — firma ma plan kont,
 *  - `hasDecreeRule` — istnieje ≥1 szablon LUB procedura dekretacji dla tej klasy.
 *
 * Steruje wyszarzeniem/chipem „Konfiguracja" w menu akcji i treścią modala naprowadzającego
 * (`ActionGuidanceModal`). Bez własnego endpointu „gotowości" — składamy z istniejących list
 * (`/ksef/connections`, `/chart-of-accounts`, `/decree-templates`, `/decree-procedures`),
 * wszystkie tenant-scope po `companyId`. Gdy brak firmy (tryb „Wszystkie firmy") — NIE bramkuj
 * (zwracamy wszystko jako gotowe), bo warunki są per firma dokumentu.
 */

import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface ActionReadiness {
  ksefConfigured: boolean;
  /** Istnieje ≥1 aktywna integracja ERP (Symfonia/FK) dla firmy. */
  fkConfigured: boolean;
  hasChart: boolean;
  hasDecreeRule: boolean;
  /** Dopóki true, nie bramkuj (unikamy migotania chipa przed załadowaniem). */
  isLoading: boolean;
}

/** Bezpieczny GET listy — 402 (brak modułu) / 403 (brak uprawnienia) → pusto, nie błąd. */
async function safeList<T>(
  path: string,
  params: Record<string, string>,
  pick: (d: unknown) => T[],
): Promise<T[]> {
  try {
    const res = await api.get(path, { params });
    return pick(res.data);
  } catch {
    return [];
  }
}

export function useActionReadiness(
  companyId: string | null,
  documentClass: string,
): ActionReadiness {
  const enabled = !!companyId;
  const cid = companyId ?? '';

  const ksefQ = useQuery({
    queryKey: ['action-readiness', 'ksef', cid],
    enabled,
    queryFn: () =>
      safeList<{ isActive?: boolean }>('/ksef/connections', { companyId: cid }, (d) =>
        Array.isArray((d as { connections?: unknown[] }).connections)
          ? (d as { connections: { isActive?: boolean }[] }).connections
          : [],
      ),
  });

  const fkQ = useQuery({
    queryKey: ['action-readiness', 'fk', cid],
    enabled,
    queryFn: () =>
      safeList<unknown>('/erp-integrations', { companyId: cid, isActive: 'true' }, (d) =>
        Array.isArray((d as { integrations?: unknown[] }).integrations)
          ? (d as { integrations: unknown[] }).integrations
          : [],
      ),
  });

  const chartQ = useQuery({
    queryKey: ['action-readiness', 'chart', cid],
    enabled,
    queryFn: () =>
      safeList<unknown>(
        '/chart-of-accounts',
        { companyId: cid, active: 'true', format: 'flat' },
        (d) => (d as { accounts?: unknown[] }).accounts ?? [],
      ),
  });

  const tplQ = useQuery({
    queryKey: ['action-readiness', 'decree-templates', cid, documentClass],
    enabled,
    queryFn: () =>
      safeList<unknown>(
        '/decree-templates',
        { companyId: cid, documentClass, active: 'true' },
        (d) => (d as { templates?: unknown[] }).templates ?? [],
      ),
  });

  const procQ = useQuery({
    queryKey: ['action-readiness', 'decree-procedures', cid, documentClass],
    enabled,
    queryFn: () =>
      safeList<unknown>(
        '/decree-procedures',
        { companyId: cid, documentClass, active: 'true' },
        (d) => (d as { procedures?: unknown[] }).procedures ?? [],
      ),
  });

  // Tryb „Wszystkie firmy" (brak companyId) — nie bramkujemy.
  if (!enabled) {
    return {
      ksefConfigured: true,
      fkConfigured: true,
      hasChart: true,
      hasDecreeRule: true,
      isLoading: false,
    };
  }

  const isLoading =
    ksefQ.isLoading || fkQ.isLoading || chartQ.isLoading || tplQ.isLoading || procQ.isLoading;

  return {
    ksefConfigured: (ksefQ.data ?? []).some((c) => c.isActive !== false),
    fkConfigured: (fkQ.data ?? []).length > 0,
    hasChart: (chartQ.data ?? []).length > 0,
    hasDecreeRule: (tplQ.data ?? []).length > 0 || (procQ.data ?? []).length > 0,
    isLoading,
  };
}
